import { ChevronRight, Folder, Globe, HardDrive, Loader2 } from "lucide-react";
import { DriveEntry } from "@forgepoint/shared";

const ENTRY_ICONS = {
  site: Globe,
  drive: HardDrive,
  folder: Folder,
};

interface DriveEntryListProps {
  entries: DriveEntry[];
  isLoading: boolean;
  error: string | null;
  emptyLabel: string;
  onOpen: (entry: DriveEntry) => void;
}

/**
 * The sites, libraries, or folders at one level of the drive browser, each a button that drills in.
 * @param props - The entries, loading and error state, and the drill-in handler
 * @returns The entry list element
 */
export function DriveEntryList({
  entries,
  isLoading,
  error,
  emptyLabel,
  onOpen,
}: DriveEntryListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return <p className="p-3 text-sm text-destructive">{error}</p>;
  }
  if (entries.length === 0) {
    return <p className="p-3 text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <ul className="duration-200 motion-safe:animate-in motion-safe:fade-in">
      {entries.map((entry) => {
        const Icon = ENTRY_ICONS[entry.kind];
        return (
          <li key={`${entry.kind}-${entry.id}`}>
            <button
              type="button"
              onClick={() => onOpen(entry)}
              className="group flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            >
              <Icon className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 break-words">{entry.name}</span>
              {(entry.kind !== "folder" || entry.hasChildren) && (
                <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground motion-reduce:transition-none" />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
